import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { adminApiService as apiService } from '../../services/adminApiService';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { ApiError } from '../../types/errorTypes';

interface Job {
  jobNumber: string;
  customerName?: string;
  contractTotal: number;
  grossProfit: number;
  grossMargin: number;
  salesCommission: number;
  salesManagerFee?: number;
  companyOH: number;
  netProfit: number;
}

interface EditPreview {
  job: {
    index: number;
    job_number: string;
    current_margin: number;
    new_margin: number;
    contract_total: number;
  };
  changes: {
    gross_profit: { current: number; new: number; delta: number };
    sales_commission: { current: number; new: number; delta: number };
    net_profit: { current: number; new: number; delta: number };
  };
  warnings: Warning[];
}

interface Warning {
  type: 'tier_change' | 'validation';
  message: string;
  severity: 'low' | 'medium' | 'high';
}

const JobEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState<'exteriors' | 'cody'>('exteriors');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [originalJobs, setOriginalJobs] = useState<Job[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [preview, setPreview] = useState<EditPreview | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    if (id) {
      fetchRawData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRawData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get(`/reports/${id}/raw-data`);
      const { data } = response;
      
      setReportName(data.report_name);
      setReportType(data.report_type);
      setJobs(data.jobs);
      setOriginalJobs(JSON.parse(JSON.stringify(data.jobs))); // Deep copy
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (index: number) => {
    setEditingIndex(index);
    setEditValue(jobs[index].grossMargin.toString());
    setPreview(null);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditValue('');
    setPreview(null);
  };

  const handlePreviewEdit = async () => {
    if (editingIndex === null) return;
    
    const newMargin = parseFloat(editValue);
    if (isNaN(newMargin) || newMargin < 0 || newMargin > 100) {
      setError('Gross margin must be between 0% and 100%');
      return;
    }

    try {
      const response = await apiService.put(`/reports/${id}/jobs/${editingIndex}/preview`, {
        grossMargin: newMargin
      });
      
      setPreview(response.data);
      setError(null);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to preview changes');
    }
  };

  const handleApplyEdit = async () => {
    if (editingIndex === null || !preview) return;
    
    try {
      const response = await apiService.put(`/reports/${id}/jobs/${editingIndex}`, {
        grossMargin: parseFloat(editValue)
      });
      
      // Update local state with new data
      const updatedJobs = [...jobs];
      const updatedJobData = response.data.updated_data.jobs[editingIndex];
      updatedJobs[editingIndex] = updatedJobData;
      setJobs(updatedJobs);
      
      // Track the change
      const newChanges = new Map(pendingChanges);
      newChanges.set(editingIndex, parseFloat(editValue));
      setPendingChanges(newChanges);
      
      // Reset edit state
      handleEditCancel();
      
      alert('Job updated successfully');
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to apply changes');
    }
  };

  const handleRegenerateReport = async () => {
    try {
      const response = await apiService.post(`/reports/${id}/regenerate`);
      const { changes } = response.data;
      
      alert(`Report regenerated successfully. 
Commission change: ${formatCurrency(changes.total_commission.delta)}
Net profit change: ${formatCurrency(changes.total_net_profit.delta)}`);
      
      // Refresh data
      fetchRawData();
      setPendingChanges(new Map());
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to regenerate report');
    }
  };

  const getCommissionTierDisplay = (margin: number): string => {
    if (reportType === 'cody') return 'N/A';
    
    if (margin < 40) return '4%';
    if (margin < 50) return '4%';
    if (margin < 60) return '7%';
    return '10%';
  };

  const hasUnsavedChanges = () => pendingChanges.size > 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Job Editor</h1>
              <p className="mt-1 text-gray-600">{reportName}</p>
              <p className="text-sm text-gray-500">
                Report Type: <span className="font-medium capitalize">{reportType}</span>
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Back to Reports
              </button>
              {hasUnsavedChanges() && (
                <button
                  onClick={() => alert('Save functionality not implemented')}
                  className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Save All Changes
                </button>
              )}
              <button
                onClick={handleRegenerateReport}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Regenerate Report
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="m-6 p-4 bg-red-50 text-red-800 rounded-lg">
            <p className="font-semibold">Error:</p>
            <p>{error}</p>
          </div>
        )}

        {/* Jobs Table */}
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contract Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gross Margin
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission Tier
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Profit
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job, index) => {
                  const isEditing = editingIndex === index;
                  const hasChanged = pendingChanges.has(index);
                  
                  return (
                    <tr key={index} className={hasChanged ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {job.jobNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {job.customerName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(job.contractTotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handlePreviewEdit}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-blue-500"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        ) : (
                          <span>
                            {formatPercentage(job.grossMargin / 100)}
                            {hasChanged && (
                              <span className="ml-1 text-xs text-yellow-600">
                                (was {formatPercentage(originalJobs[index].grossMargin / 100)})
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          job.grossMargin >= 60 ? 'bg-green-100 text-green-800' :
                          job.grossMargin >= 50 ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getCommissionTierDisplay(job.grossMargin)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(job.salesCommission)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(job.netProfit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        {isEditing ? (
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={handleApplyEdit}
                              disabled={!preview}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              Apply
                            </button>
                            <button
                              onClick={handleEditCancel}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditStart(index)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Edit Preview */}
          {preview && (
            <div className="mt-6 p-6 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Edit Preview</h3>
              
              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div className="mb-4 space-y-2">
                  {preview.warnings.map((warning, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start p-3 rounded-lg ${
                        warning.severity === 'high' ? 'bg-red-100 text-red-800' :
                        warning.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}
                    >
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{warning.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Financial Impact */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Gross Profit</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">
                      {formatCurrency(preview.changes.gross_profit.new)}
                    </span>
                    <span className={`flex items-center text-sm ${
                      preview.changes.gross_profit.delta > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {preview.changes.gross_profit.delta > 0 ? 
                        <ArrowUpIcon className="h-3 w-3 mr-1" /> : 
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                      }
                      {formatCurrency(Math.abs(preview.changes.gross_profit.delta))}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Sales Commission</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">
                      {formatCurrency(preview.changes.sales_commission.new)}
                    </span>
                    <span className={`flex items-center text-sm ${
                      preview.changes.sales_commission.delta > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {preview.changes.sales_commission.delta > 0 ? 
                        <ArrowUpIcon className="h-3 w-3 mr-1" /> : 
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                      }
                      {formatCurrency(Math.abs(preview.changes.sales_commission.delta))}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Net Profit</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">
                      {formatCurrency(preview.changes.net_profit.new)}
                    </span>
                    <span className={`flex items-center text-sm ${
                      preview.changes.net_profit.delta > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {preview.changes.net_profit.delta > 0 ? 
                        <ArrowUpIcon className="h-3 w-3 mr-1" /> : 
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                      }
                      {formatCurrency(Math.abs(preview.changes.net_profit.delta))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary Stats */}
          {jobs.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Jobs:</span>
                  <span className="ml-2 font-medium">{jobs.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Contract:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(jobs.reduce((sum, job) => sum + job.contractTotal, 0))}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Commission:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(jobs.reduce((sum, job) => sum + job.salesCommission, 0))}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Net Profit:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(jobs.reduce((sum, job) => sum + job.netProfit, 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobEditor;